import { trace } from "firebase/performance";

import { getPerformanceInstance } from "../firebase";
import { AddProduct } from "../types/Action";
import {
    AddProductError,
    ApplyCouponError,
    Cart,
    CheckoutError,
    CheckoutResponse,
    DeleteLineItemError,
    IdentityDocument,
    Session,
    StartError,
    UpdateLineItemError,
    VerifyAgeWithIdentityDocumentError
} from "../types/Cart";
import { partitionArray } from "../utils/partitionArray";

const catchError = async <E extends { case: string }>(response: Response) => {
    if (response.ok) return response;

    if (response.status >= 500) {
        throw { case: "Unknown" } as E;
    }

    try {
        const error = (await response.json()) as E;
        return Promise.reject(error);
    } catch {
        throw { case: "Unknown" } as E;
    }
};

export const getEventPayload = (event: AddProduct) => {
    switch (event.payload.kind) {
        case "scanned":
            return {
                barcode: event.payload.code,
                quantity: 1,
                source: "scan",
                device: event.payload.device
            };
        case "manuallyAdded":
            return {
                barcode: event.payload.code,
                quantity: 1,
                source: event.payload.analyticsSource
            };
        default:
            return event.payload satisfies never;
    }
};

type AddProductParameters = {
    barcode: string;
    quantity?: number;
    fromKitchen?: boolean;
    addOnOptionIds?: string[];
};

type UpdateLineItemParameters = { id: string; quantity: number };
type DeleteComboParameters = { id: string; quantity: number };

export class SessionClient {
    constructor(private client: typeof fetch) {}

    public async checkin(): Promise<Session> {
        const t = trace(getPerformanceInstance(), "checkin");
        t.start();
        const session = await this.client("/ezio/v1/start", {
            method: "POST",
            headers: { version: import.meta.env.VITE_VERSION }
        })
            .then((res) => catchError<StartError>(res))
            .then((response) => response.json());
        t.stop();

        return session;
    }
}

export class CartClient {
    constructor(private client: typeof fetch) {}

    public async add(parameters: AddProductParameters[]): Promise<Cart> {
        const t = trace(getPerformanceInstance(), "addProducts");
        t.start();

        const cart = await this.client("/ezio/v1/cart/products:batch", {
            method: "POST",
            body: JSON.stringify(parameters)
        })
            .then((res) => catchError<AddProductError>(res))
            .then((response) => response.json());

        t.stop();

        return cart;
    }

    public async applyCoupon(code: string): Promise<Cart> {
        const t = trace(getPerformanceInstance(), "applyCoupon");
        t.start();

        const cart = await this.client("/ezio/v1/cart/coupon", {
            method: "PUT",
            body: JSON.stringify({
                code
            })
        })
            .then((res) => catchError<ApplyCouponError>(res))
            .then((response) => response.json());
        t.stop();

        return cart;
    }

    public async updateLineItems(
        lineItems: UpdateLineItemParameters[]
    ): Promise<Cart> {
        const cart = await this.client(`/ezio/v1/cart/line-items:batch`, {
            method: "PUT",
            body: JSON.stringify({ lineItems })
        })
            .then((res) => catchError<UpdateLineItemError>(res))
            .then((response) => response.json());

        return cart;
    }

    public async deleteCombo(updates: DeleteComboParameters[]): Promise<Cart> {
        const [toDelete, toUpdate] = partitionArray(
            updates,
            ({ quantity }) => quantity === 0
        );

        const newCart = await this.client("/ezio/v1/cart/line-items:batch", {
            method: "DELETE",
            body: JSON.stringify({
                lineItemIds: toDelete.map(({ id }) => id)
            })
        })
            .then(() =>
                this.client("/ezio/v1/cart/line-items:batch", {
                    method: "PUT",
                    body: JSON.stringify({ lineItems: toUpdate })
                })
            )
            .then((res) => catchError<DeleteLineItemError>(res))
            .then((response) => response.json());

        return newCart;
    }

    public async deleteLineItems(lineItemIds: string[]): Promise<Cart> {
        const cart = await this.client(`/ezio/v1/cart/line-items:batch`, {
            method: "DELETE",
            body: JSON.stringify({ lineItemIds })
        })
            .then((res) => catchError<DeleteLineItemError>(res))
            .then((response) => response.json());

        return cart;
    }

    public async scanId(identityDocument: IdentityDocument): Promise<Cart> {
        const response = await this.client(`/ezio/v1/cart/identity`, {
            method: "POST",
            body: JSON.stringify(identityDocument)
        })
            .then((res) => catchError<VerifyAgeWithIdentityDocumentError>(res))
            .then((response) => response.json());

        return response;
    }

    public async skipAgeVerification(): Promise<Cart> {
        const cart = await this.client(`/ezio/v1/cart/identity/skip`, {
            method: "POST"
        }).then((response) => response.json());

        return cart;
    }

    public async operatorScannedCartCheck(): Promise<void> {
        await this.client(`/ezio/v1/cart-check/operator-scan`, {
            method: "POST"
        });
    }

    public async checkout(language: string, simulatePaymentResponse?: string) {
        const debug = simulatePaymentResponse
            ? `?simulatePaymentResponse=${simulatePaymentResponse}`
            : "";
        const t = trace(getPerformanceInstance(), "checkout");
        t.start();
        const response: CheckoutResponse = await this.client(
            `/ezio/v1/checkout${debug}`,
            {
                method: "POST",
                headers: {
                    "Accept-Language": language
                },
                signal: AbortSignal.timeout(100_000)
            }
        )
            .then((res) => catchError<CheckoutError>(res))
            .then((result) => result.json());
        t.stop();
        return response;
    }
}
